import React from 'react';
import { useTranslation } from 'react-i18next';
import { optClasses, OptToken } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { useListFetch } from '../useListFetch';

/**
 * KpiRow — Part 3 of the real-estate module (realestate-dashboard-prd.md §3). Replaces the 5 mock
 * KpiTile constants PortfolioTab used to render with ONE real read (GET /api/properties/kpis),
 * account-wide across ALL Properties (§3.1 ruling 2). Reads Part 1 (Invoice/rent) + Part 2 (Tickets)
 * data via its own server-side query (PropertyService.getPortfolioKpis) — this file owns its own
 * fetch, not Part 1/2's UI.
 *
 * ONE shared loading/error state for the whole row (not 5 independent skeletons) — a tile that failed
 * to load is a data-integrity problem for the whole row, not "one card was slower than the others".
 *
 * No fabricated trend deltas: the mock's "▲ 1.4% vs last month" had no history to back it, so it's
 * gone — Occupancy's sub-line is the history-free "N of M units" instead. No tile is click-to-filter
 * (not asked).
 */

interface PortfolioKpis {
  // I6 §R3 — trendPts/trendPct are null whenever there's no honest prior-month figure to compare
  // against (AC6.3's degenerate-zero rule): the sub-line then shows nothing extra, never a NaN/
  // +Infinity% artifact.
  occupancy: { occupiedUnits: number; totalUnits: number; trendPts: number | null };
  rent: { collected: number; billed: number; trendPct: number | null };
  overdue: { amount: number; unitsCount: number; followUpCount: number };
  leasesExpiring30d: { count: number; renewalsSentCount: number };
  openWorkOrders: { count: number; fromWhatsAppCount: number };
}

const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtTrend = (n: number) => (n > 0 ? `+${n}` : `${n}`);

interface TileProps { testId: string; label: string; value: string; sub: string; subToken?: OptToken; trend?: string }
const Tile: React.FC<TileProps> = ({ testId, label, value, sub, subToken, trend }) => (
  <div className="bg-surface border border-border-neutral rounded-xl p-4" data-testid={testId}>
    <div className="text-xs text-text-muted">{label}</div>
    <div className="text-2xl font-bold text-text-strong mt-2" dir="ltr">{value}</div>
    <div className={`text-xs mt-1 ${subToken ? optClasses(subToken).fg : 'text-text-muted'}`}>{sub}</div>
    {trend && <div className="text-xs text-text-muted mt-0.5" dir="ltr" data-testid={`${testId}-trend`}>{trend}</div>}
  </div>
);

export const KpiRow: React.FC = () => {
  const { t } = useTranslation('common');
  const { status, data, retry } = useListFetch<PortfolioKpis>('/api/properties/kpis');

  // Degenerate-zero honesty (§3): a division only happens for occupancy% and rent%, and only when
  // there's something to divide by — never 0/0 or NaN, each tile falls back to an honest empty state.
  const occPct = data && data.occupancy.totalUnits > 0
    ? Math.round((data.occupancy.occupiedUnits / data.occupancy.totalUnits) * 100)
    : null;
  const rentPct = data && data.rent.billed > 0
    ? Math.round((data.rent.collected / data.rent.billed) * 100)
    : null;

  return (
    <ListStateShell status={status} testIdBase="portfolio-kpis" onRetry={retry}>
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="portfolio-kpis">
          <Tile
            testId="portfolio-kpi-occupancy"
            label={t('portfolio.kpis.occupancy.title')}
            value={occPct != null ? `${occPct}%` : '—'}
            sub={
              data.occupancy.totalUnits > 0
                ? t('portfolio.kpis.occupancy.sub', { occupied: data.occupancy.occupiedUnits, total: data.occupancy.totalUnits })
                : t('portfolio.kpis.occupancy.empty')
            }
            trend={data.occupancy.trendPts != null ? t('portfolio.kpis.occupancy.trend', { pts: fmtTrend(data.occupancy.trendPts) }) : undefined}
          />
          <Tile
            testId="portfolio-kpi-rent"
            label={t('portfolio.kpis.rent.title')}
            value={data.rent.billed > 0 ? fmtMoney(data.rent.collected) : '—'}
            sub={
              data.rent.billed > 0
                ? t('portfolio.kpis.rent.sub', { billed: fmtMoney(data.rent.billed), pct: rentPct })
                : t('portfolio.kpis.rent.empty')
            }
            trend={data.rent.trendPct != null ? t('portfolio.kpis.rent.trend', { pct: fmtTrend(data.rent.trendPct) }) : undefined}
          />
          <Tile
            testId="portfolio-kpi-overdue"
            label={t('portfolio.kpis.overdue.title')}
            value={fmtMoney(data.overdue.amount)}
            subToken={data.overdue.unitsCount > 0 ? 'rose' : undefined}
            sub={
              data.overdue.unitsCount > 0
                ? t('portfolio.kpis.overdue.sub', { units: data.overdue.unitsCount, followUp: data.overdue.followUpCount })
                : t('portfolio.kpis.overdue.empty')
            }
          />
          <Tile
            testId="portfolio-kpi-leases-expiring"
            label={t('portfolio.kpis.leasesExpiring.title')}
            value={String(data.leasesExpiring30d.count)}
            subToken={data.leasesExpiring30d.count > 0 ? 'amber' : undefined}
            sub={
              data.leasesExpiring30d.count > 0
                ? t('portfolio.kpis.leasesExpiring.sub', { count: data.leasesExpiring30d.renewalsSentCount })
                : t('portfolio.kpis.leasesExpiring.empty')
            }
          />
          <Tile
            testId="portfolio-kpi-work-orders"
            label={t('portfolio.kpis.openWorkOrders.title')}
            value={String(data.openWorkOrders.count)}
            sub={
              data.openWorkOrders.count > 0
                ? t('portfolio.kpis.openWorkOrders.sub', { count: data.openWorkOrders.fromWhatsAppCount })
                : t('portfolio.kpis.openWorkOrders.empty')
            }
          />
        </div>
      )}
    </ListStateShell>
  );
};
